import type {
  PushPublicKeyResponse,
  PushSubscribeRequest,
  PushUnsubscribeRequest,
} from '@wit-support/admin-contracts';
import { env } from '../env.js';

/**
 * Web Push subscription helper. Kept in a single module so the AppShell's
 * menu item stays declarative — it just calls `subscribe()` / `unsubscribe()`.
 *
 * Flow (subscribe):
 *   1. Check browser support and current permission.
 *   2. Fetch the server's VAPID public key.
 *   3. Call `registration.pushManager.subscribe({ userVisibleOnly: true })`.
 *   4. POST the subscription to admin-api.
 *
 * iOS caveat (Safari 16.4+): `pushManager.subscribe` rejects until the PWA is
 * installed to the Home Screen and launched at least once. We surface a
 * dedicated error so callers can show a hint.
 */

export type PushStatus =
  | { kind: 'unsupported' }
  | { kind: 'needs-install' }
  | { kind: 'denied' }
  | { kind: 'subscribed'; endpoint: string }
  | { kind: 'not-subscribed' };

export class PushNotSupportedError extends Error {}
export class PushNeedsInstallError extends Error {}
export class PushDeniedError extends Error {}

const apiBase = env.VITE_ADMIN_API_URL;

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** iOS only allows push from installed home-screen PWAs (display-mode standalone). */
function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent);
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export async function getPushStatus(): Promise<PushStatus> {
  if (!isPushSupported()) return { kind: 'unsupported' };
  if (isIOS() && !isStandalone()) return { kind: 'needs-install' };
  if (Notification.permission === 'denied') return { kind: 'denied' };
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return sub ? { kind: 'subscribed', endpoint: sub.endpoint } : { kind: 'not-subscribed' };
}

async function fetchPublicKey(): Promise<string> {
  const res = await fetch(`${apiBase}/api/push/public-key`, { credentials: 'include' });
  if (!res.ok) throw new Error(`public-key ${res.status}`);
  const data = (await res.json()) as PushPublicKeyResponse;
  return data.vapidPublicKey;
}

/** VAPID keys are URL-safe base64; PushManager wants a Uint8Array. */
function urlBase64ToUint8Array(b64: string): Uint8Array {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function subscribe(): Promise<void> {
  if (!isPushSupported()) throw new PushNotSupportedError('Push not supported in this browser');
  if (isIOS() && !isStandalone()) {
    throw new PushNeedsInstallError('Install the app to your Home Screen first');
  }
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new PushDeniedError('Permission denied');

  const reg = await navigator.serviceWorker.ready;
  const publicKey = await fetchPublicKey();
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey).buffer as ArrayBuffer,
    }));

  const json = sub.toJSON() as {
    endpoint: string;
    keys?: { p256dh?: string; auth?: string };
  };
  const body: PushSubscribeRequest = {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
  };
  const res = await fetch(`${apiBase}/api/push/subscriptions`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`subscribe ${res.status}`);
}

export async function unsubscribe(): Promise<void> {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;

  // Track outcomes so a total failure can throw — the AppShell's disable
  // flow surfaces a toast when unsubscribe rejects. Silently swallowing both
  // errors previously left the UI looking "unsubscribed" while the server
  // still held an active subscription, which triggered orphan-push bugs.
  let browserOk = true;
  let serverOk = true;

  try {
    await sub.unsubscribe();
  } catch (e) {
    browserOk = false;
    console.warn('push: browser unsubscribe failed', e);
  }

  try {
    const body: PushUnsubscribeRequest = { endpoint };
    const res = await fetch(`${apiBase}/api/push/subscriptions`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      serverOk = false;
      console.warn('push: server unsubscribe failed', res.status);
    }
  } catch (e) {
    serverOk = false;
    console.warn('push: server unsubscribe fetch failed', e);
  }

  if (!browserOk && !serverOk) {
    throw new Error('push: unsubscribe failed on both browser and server');
  }
}
